/**
 * Räume = autonome Prozesse (Eingang/Ausgang über Artefakte). KANONISCHE QUELLE.
 *
 * Blueprint §1-2: ein Raum deklariert SELBST, welche Artefakte er im Twin braucht
 * (needs) und welches er ausstellt (produces). Keine Prozess-Kopplung, keine
 * „Vorgänger done"-Annahme — die Kette ist emergent (produces(N) ist needs(N+1)).
 * Status aus der tatsächlichen Twin-Mappe; Druck-Prinzip: fehlt ein benötigtes
 * Artefakt, ist der Raum not-reached (der Twin kam nie an).
 *
 * ⚠ MIRROR in hub/src/lib/pipeline-gates.ts (artifacts.ts + diese Logik). BEIDE angleichen.
 */
import { type CardLike, type CardKind, type ArtifactId, detectArtifact } from "./artifacts";

export type { CardLike, CardKind } from "./artifacts";
export { readTwinVersion, CURRENT_SCHEMA_VERSION, twinDossier } from "./artifacts";

export type RoomStatus = "done" | "active" | "error" | "not-reached";

export interface RoomDef {
  id: string;            // = PhaseSlug
  label: string;
  needs: ArtifactId[];   // Eingangskriterium: diese Pässe müssen im Twin liegen
  produces: ArtifactId;  // Ausgang: dieses Dokument stellt der Raum aus
  /** Erreicht (needs erfüllt), aber kein Ausgang + nicht „läuft noch" → gescheitert. */
  failed?: (c: CardLike) => boolean;
}

export interface RoomState {
  id: string;
  label: string;
  needs: ArtifactId[];
  produces: ArtifactId;
  status: RoomStatus;
  version_ok: boolean;
  needs_migration: boolean;
  reason: string;
  missing?: ArtifactId[]; // welche needs fehlen (bei not-reached)
}

const TERMINAL = new Set(["archived", "superseded"]);
const arr = (x: any) => (Array.isArray(x) ? x : []);

// Gemeinsame Kette (Artefakt-verkettet): produces(N) == needs(N+1).
function rooms(kind: CardKind): RoomDef[] {
  const ocrLabel = kind === "expense" || kind === "unknown" ? "OCR — Tabelle → Posten" : "Per-Cell-OCR (data.days)";
  const ocrFailed = (c: CardLike) => {
    const extractEmpty = kind === "expense" || kind === "unknown"
      ? arr(c.data?.entries).length === 0
      : !(c.data?.days && Object.keys(c.data.days).some((k) => /^\d+$/.test(k)));
    return extractEmpty && (c.state === "pending_review" || TERMINAL.has(c.state));
  };
  const term = (c: CardLike) => TERMINAL.has(c.state);
  return [
    { id: "upload", label: "Upload + Triage (Routing)", needs: [], produces: "routed",
      failed: (c) => c.kind === "unknown" },
    { id: "ocr", label: ocrLabel, needs: ["routed"], produces: "ocr_extract", failed: ocrFailed },
    { id: "validierung", label: "Validierung (Mitarbeiter + Bestätigung)", needs: ["ocr_extract"], produces: "employee_assigned", failed: term },
    { id: "berechnung", label: kind === "expense" || kind === "unknown" ? "Tages-Aggregation (Tagessatz)" : "KV-Berechnung", needs: ["employee_assigned"], produces: "calculation", failed: term },
    { id: "review", label: "Review", needs: ["calculation"], produces: "reviewed", failed: term },
    { id: "export", label: "RZL-CSV-Export", needs: ["reviewed"], produces: "exported" },
  ];
}

export function roomsForKind(kind: CardKind | null | undefined): RoomDef[] {
  return rooms((kind ?? "timesheet") as CardKind);
}

/**
 * Twin-Reise: jeder Raum prüft AUTONOM seine Artefakt-Anforderung gegen den Twin.
 * Emergent: ein Raum ohne erfüllte needs ist not-reached (der vom Vorgänger
 * auszustellende Pass fehlt). Erster Raum „needs erfüllt, produces fehlt" = Hängepunkt.
 */
export function computeJourney(card: CardLike, kind: CardKind): RoomState[] {
  const out: RoomState[] = [];
  for (const r of roomsForKind(kind)) {
    const missing = r.needs.filter((n) => !detectArtifact(card, n).present);
    const needsMet = missing.length === 0;
    const prod = detectArtifact(card, r.produces);
    let status: RoomStatus;
    let reason: string;
    let needsMig = false;
    if (!needsMet) {
      status = "not-reached";
      reason = `Braucht Artefakt: ${missing.join(", ")} (fehlt im Twin)`;
    } else if (prod.present) {
      status = "done";
      needsMig = prod.stale;
      reason = prod.stale
        ? `„${r.produces}" vorhanden, aber Twin-Version ${prod.version} ungestempelt → Lazy-Migration`
        : `Ausgang „${r.produces}" ausgestellt`;
    } else if (r.failed ? safe(() => r.failed!(card)) : false) {
      status = "error";
      reason = `Eingang erfüllt, aber „${r.produces}" nicht ausgestellt — im Raum gestoppt`;
    } else {
      status = "active";
      reason = `Eingang erfüllt, „${r.produces}" läuft noch`;
    }
    out.push({ id: r.id, label: r.label, needs: r.needs, produces: r.produces,
      status, version_ok: !prod.stale, needs_migration: needsMig, reason,
      missing: status === "not-reached" ? missing : undefined });
  }
  return out;
}

function safe(fn: () => boolean) { try { return !!fn(); } catch { return false; } }

/** Konflikt-Phase ist PARALLEL: nur done wenn ein Konflikt auflief+gelöst wurde;
 *  pending_conflict = error; sonst not-reached (n/a) — kein Falsch-Grün. */
export function konfliktStatus(card: CardLike): RoomStatus {
  if (card.state === "pending_conflict") return "error";
  if (card.data?.conflict_resolution || card.data?.resolved_conflict) return "done";
  return "not-reached";
}

export function roomStatus(card: CardLike, kind: CardKind, slug: string): RoomState | null {
  if (slug === "konflikt") {
    return { id: "konflikt", label: "Konflikt", needs: [], produces: "exported",
      status: konfliktStatus(card), version_ok: true, needs_migration: false, reason: "" };
  }
  return computeJourney(card, kind).find((r) => r.id === slug) ?? null;
}
